import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { StandardSnack } from '@components/snackbar';
import {
  CustomDateIcon,
  CustomEmailIcon,
  CustomMultiselectIcon,
  CustomNumberIcon,
  CustomPersonIcon,
  CustomPhoneIcon,
  CustomSelectIcon,
  CustomTextIcon,
  CustomUrlIcon,
  DeleteIcon,
  DuplicateIcon,
  EditIcon,
  MoreIcon,
} from '@sketchnote/icons';
import { MoreActionMenu } from '@sketchnote/shared-components';
import { LoaderXs } from '@sketchnote/shared-components';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { API } from 'src/api';
import { useTasksStore } from 'src/features/tasks/store/useTasksStore';
import { serialize } from 'src/utils/serialize';

import { TaskT } from '../../../features/tasks/types/tasks';
import { useFileExploreStore } from '../store';
import { ExploreHeader } from './home';
import { TemplateEdit } from './task-template-edit';

export const TaskTemplate = () => {
  const { closeExploreMenu, setNavigation } = useFileExploreStore();

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [taskTemplates, setTaskTemplates] = useState<any>([]);
  const { tasks, setTask, unsetTemplate } = useTasksStore();

  const handleCloseMenu = () => {
    closeExploreMenu();
  };
  const handleOnBack = () => {
    setNavigation('home');
  };

  useEffect(() => {
    const getAllTaskTemplates = async () => {
      const response = await API.get(`/board/task/template?page=${page}&limit=10`);
      const { docs, hasNextPage } = formatTemplate(response.data.data);
      setHasMore(hasNextPage);
      docs.map((item) => {
        setTask(item.id ?? '', item);
      });

      setTaskTemplates([...taskTemplates, ...docs]);
    };
    getAllTaskTemplates();
  }, [page]);

  useEffect(() => {
    const templateIds = Object.keys(tasks);
    const templates: Partial<TaskT>[] = [];
    const result = templateIds.reduce((prev, curr) => {
      const template = tasks[curr];
      if (template && !template.board) {
        prev.push(template);
      }
      return prev;
    }, templates);
    setTaskTemplates([...result]);
  }, [tasks]);

  const handleFetchTaskTemplates = () => {
    setPage(page + 1);
  };

  const deleteTemplate = async (id: string) => {
    try {
      const response = await API.delete(`/board/task/template?id=${id}`);
      if (response) {
        unsetTemplate(id);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const formatTemplate = (data) => {
    const docs = data.docs.map((item) => {
      item = { id: item._id, ...item };
      item.fields = item.fields.map((field) => {
        return { id: field._id, ...field };
      });
      return item;
    });
    const hasNextPage = data.hasNextPage;
    return { docs, hasNextPage };
  };

  return (
    <div>
      <div className="">
        <ExploreHeader title="" onBackClick={handleOnBack} onCloseClick={handleCloseMenu} />
      </div>
      <div className="mb-24 overflow-none">
        <span className="text-gray-999 mb-6 font-medium text-lg">💼️ Task Templates</span>
        <p className="text-sm text-gray-600">Edit or duplicate task templates</p>
      </div>
      {taskTemplates.length === 0 ? (
        <div className="mb-10">
          <EmptyState contents={['Create task templates to fill up this area!']} />
        </div>
      ) : (
        <div
          className={cn('overflow-hidden overflow-y-scroll no-scrollbar h-599')}
          id="scrollableDiv"
        >
          <InfiniteScroll
            dataLength={taskTemplates.length}
            next={handleFetchTaskTemplates}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
            loader={
              <div>
                <LoaderXs className="mx-auto text-xss m-10" />
              </div>
            }
            endMessage={
              <div className="flex items-center justify-center pt-10 pb-10">
                <StandardSnack text="That’s all, folks!" />
              </div>
            }
          >
            {taskTemplates.length > 0 &&
              taskTemplates.map((item, index) => {
                return <TemplateItem item={item} key={index} deleteTemplate={deleteTemplate} />;
              })}
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
};

export const TemplateItem = ({
  item,
  deleteTemplate,
}: {
  item: Partial<TaskT>;
  deleteTemplate: (string) => void;
}) => {
  useEffect(() => {
    useTasksStore.setState({ currentTaskId: item.id });
    return () => useTasksStore.setState({ currentTaskId: undefined });
  }, [item.id]);

  const menuItems = [
    {
      label: (
        <div className="flex font-regular text-gray-999 text-sm ">
          <EditIcon fill="#878A92" wrapperClass="mt-4 text-gray-500" />
          &nbsp;&nbsp; Edit template
        </div>
      ),
      value: 'edit',
    },
    {
      label: (
        <div className="flex font-regular text-gray-999 text-sm ">
          <DuplicateIcon wrapperClass="mt-4 text-gray-500" />
          &nbsp;&nbsp; Duplicate
        </div>
      ),
      value: 'duplicate',
    },
    {
      label: (
        <div className="flex font-regular text-gray-999 text-sm ">
          <DeleteIcon wrapperClass="mt-4 text-gray-500" />
          &nbsp;&nbsp; Delete
        </div>
      ),
      value: 'delete',
    },
  ];

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // const { duplicateTaskTemplate } = useTasksStore();

  const handleMenuItemClick = (e) => {
    if (e.value === 'delete') setIsDeleteModalOpen(true);
    else if (e.value === 'edit') {
      setIsEditModalOpen(true);
    } else if (e.value === 'duplicate') {
      // item?.id && duplicateTaskTemplate(item?.id);
    }
  };

  const handleDeleteTemplate = async () => {
    item?.id && deleteTemplate(item.id);
    setIsDeleteModalOpen(false);
  };
  return (
    <div className="mb-16 border-b">
      <div className="flex flex-row justify-between mb-4">
        {item && (
          <span className="font-regular text-gray-600 text-md truncate">
            {Array.isArray(item.name) ? serialize(item.name) : item.name}
          </span>
        )}
        <span>
          <MoreActionMenu
            onClickMenuItem={handleMenuItemClick}
            classNames={{
              menuWrap: 'rounded-lg shadow-sm',
            }}
            icon={<MoreIcon className="text-gray-500" />}
            menuItems={menuItems}
          />
        </span>
      </div>
      <div className="flex items-center mb-16">
        {item &&
          item.fields?.slice(0, 3).map((field, index) => {
            return (
              <span className="flex mr-6 items-center" key={index}>
                {field.type === 'text' ? (
                  <CustomTextIcon className="text-gray-400" />
                ) : field.type === 'number' ? (
                  <CustomNumberIcon className="text-gray-400" />
                ) : field.type === 'date' ? (
                  <CustomDateIcon className="text-gray-400" />
                ) : field.type === 'select' ? (
                  <CustomSelectIcon className="text-gray-400" />
                ) : field.type === 'multiselect' ? (
                  <CustomMultiselectIcon className="text-gray-400" />
                ) : field.type === 'url' ? (
                  <CustomUrlIcon className="text-gray-400" />
                ) : field.type === 'email' ? (
                  <CustomEmailIcon className="text-gray-400" />
                ) : field.type === 'phone' ? (
                  <CustomPhoneIcon className="text-gray-400" />
                ) : field.type === 'person' ? (
                  <CustomPersonIcon className="text-gray-400" />
                ) : null}
                <div className="flex font-regular text-gray-600 text-xss ml-4 truncate">
                  {field.label}
                </div>
              </span>
            );
          })}
        {item?.fields && item?.fields?.length > 3 ? (
          <div className="flex ml-4 h-18 text-xs items-center ">+ {item.fields?.length - 3}</div>
        ) : null}
      </div>
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteTemplate}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '66.7rem',
            maxHeight: '76.8rem',
            borderRadius: '8px',
          }}
          className="min-w-768 min-h-vh70 bg-white rounded-lg static"
        >
          <TemplateEdit item={item} setIsEditModalOpen={setIsEditModalOpen} />
        </ModalContent>
      </Modal>
    </div>
  );
};

const EmptyState = ({ contents }: { contents: string[] }) => (
  <div className="flex justify-center text-md text-center p-10 mt-40 text-gray-999 w-full bg-gray-100 border-gray-200 rounded border">
    {contents.map((obj, index) => (
      <p key={`${obj}${index}`}>{obj}</p>
    ))}
  </div>
);
